export const PRICE_ALERT_NUMBER_BUTTON_VARIANT = {
  GREEN: "green",
  RED: "red",
  DEFAULT: "default"
};

export const PRICE_ALERT_NUMBER_BUTTON_COLORS = {
  [PRICE_ALERT_NUMBER_BUTTON_VARIANT.GREEN]: "#CDECE3",
  [PRICE_ALERT_NUMBER_BUTTON_VARIANT.RED]: "#FCD8D8",
  [PRICE_ALERT_NUMBER_BUTTON_VARIANT.DEFAULT]: "#0605341A"
};

export const PRICE_ALERT_NUMBER_TEXT_COLORS = {
  [PRICE_ALERT_NUMBER_BUTTON_VARIANT.GREEN]: "#07A172",
  [PRICE_ALERT_NUMBER_BUTTON_VARIANT.RED]: "#F03F3F"
};

/**
 * Map attribute_name → button variant.
 * Attributes omitted here resolve to DEFAULT.
 */
/** Trend signals stop-loss column. Same colors as stop_loss. */
export const TRAILING_STOP_LOSS_ATTRIBUTE_NAME = "trailing_sl";

export const PRICE_ALERT_NUMBER_BUTTON_VARIANT_BY_ATTRIBUTE = {
  stop_loss: PRICE_ALERT_NUMBER_BUTTON_VARIANT.RED,
  take_profit: PRICE_ALERT_NUMBER_BUTTON_VARIANT.GREEN,
  [TRAILING_STOP_LOSS_ATTRIBUTE_NAME]: PRICE_ALERT_NUMBER_BUTTON_VARIANT.RED
};

/** Backend values_format that activates NumberWithPriceAlert (values_type stays "number"). */
export const NUMBER_WITH_PRICE_ALERT_FORMAT = "number_with_price_alert";

/** NumberHelper format used to display the cell value. */
export const NUMBER_WITH_PRICE_ALERT_DISPLAY_FORMAT = "number_two_decimal";

export const PRICE_ALERT_COMMENT_COLUMN_ATTRIBUTE_NAME = "price_alert_comment";

export function shouldUseNumberWithPriceAlertRenderer(format) {
  return format === NUMBER_WITH_PRICE_ALERT_FORMAT;
}

export function isEmptyNumberWithPriceAlertValue(value) {
  if (value === null || typeof value === "undefined") {
    return true;
  }

  if (typeof value === "number") {
    return Number.isNaN(value);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();

    if (!trimmed || trimmed === "-" || trimmed.toLowerCase() === "none") {
      return true;
    }

    return Number.isNaN(Number(trimmed));
  }

  return false;
}

export function getNumberWithPriceAlertTicker(resultMetaData) {
  if (!resultMetaData || typeof resultMetaData !== "object") {
    return "";
  }

  const raw = resultMetaData.ticker ?? resultMetaData.stock__ticker;

  if (typeof raw !== "string") {
    return "";
  }

  const ticker = raw.trim();

  if (!ticker || ticker === "ticker_as_link") {
    return "";
  }

  return ticker;
}

export function getPriceAlertNumberButtonVariant(attributeName) {
  return (
    PRICE_ALERT_NUMBER_BUTTON_VARIANT_BY_ATTRIBUTE[attributeName] ||
    PRICE_ALERT_NUMBER_BUTTON_VARIANT.DEFAULT
  );
}

export function getPriceAlertNumberButtonStyle(variant) {
  const backgroundColor =
    PRICE_ALERT_NUMBER_BUTTON_COLORS[variant] ||
    PRICE_ALERT_NUMBER_BUTTON_COLORS[PRICE_ALERT_NUMBER_BUTTON_VARIANT.DEFAULT];

  return { backgroundColor };
}

export function getPriceAlertNumberTextStyle(variant) {
  const color = PRICE_ALERT_NUMBER_TEXT_COLORS[variant];
  return color ? { color } : undefined;
}

function firstNonBlankString(...values) {
  for (const value of values) {
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }

  return "";
}

function resolvePriceAlertCommentKey(resultMetaData) {
  return firstNonBlankString(
    resultMetaData?.[PRICE_ALERT_COMMENT_COLUMN_ATTRIBUTE_NAME],
    resultMetaData?.priceAlertComment
  );
}

export function buildNumberWithPriceAlertDescription({
  resultMetaData,
  label,
  translate
} = {}) {
  if (!resultMetaData || typeof resultMetaData !== "object") {
    return undefined;
  }

  const commentKey = resolvePriceAlertCommentKey(resultMetaData);

  if (!commentKey) {
    return undefined;
  }

  const translated =
    typeof translate === "function" ? translate(commentKey) : commentKey;
  const labelPart = typeof label === "string" ? label.trim() : "";
  const ticker = getNumberWithPriceAlertTicker(resultMetaData);
  const description = [translated, ticker, labelPart]
    .filter(Boolean)
    .join(" - ");

  return description || undefined;
}

export function pickNumberWithPriceAlertModalFields(
  resultMetaData,
  value,
  { label, translate } = {}
) {
  if (!resultMetaData || typeof resultMetaData !== "object") {
    return {
      stockId: null,
      stockName: undefined,
      price: value,
      description: undefined
    };
  }

  const stockId =
    resultMetaData.stock_id !== null &&
    typeof resultMetaData.stock_id !== "undefined"
      ? resultMetaData.stock_id
      : resultMetaData.stockId ?? null;

  const stockNameRaw =
    resultMetaData.company_name ?? resultMetaData.companyName ?? "";
  const stockName =
    typeof stockNameRaw === "string" ? stockNameRaw.trim() : undefined;

  return {
    stockId,
    stockName: stockName || undefined,
    price: value,
    description: buildNumberWithPriceAlertDescription({
      resultMetaData,
      label,
      translate
    })
  };
}
